import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { testimonials } from "@/mocks/movies";
import { useState, useCallback } from "react";

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const contactFormUrl = "https://readdy.ai/api/form/d9mtl2kit35ii3jdllrg";

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      return;
    }
    formData.delete("phone_alt");

    try {
      const res = await fetch(contactFormUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || "提交失败";
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  }, []);

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "8090电影网",
    url: "https://8090movie.com",
    description: "8090电影网怀旧影视专区，专注于80后经典电影和90后电视剧的怀旧影视内容平台。",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于8090电影网",
    description: "8090电影网是一个面向80后、90后群体的怀旧影视内容平台，致力于收录和分享80年代经典电影、90年代经典电视剧等怀旧内容。",
    url: "https://8090movie.com/about",
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 8090电影网 | 品牌介绍与联系方式"
        description="8090电影网怀旧影视专区，致力于收录80后经典电影和90后电视剧。了解我们的品牌故事、口碑评价与联系方式，一起重温童年回忆。"
        keywords="8090电影网,关于我们,怀旧影视,品牌介绍,联系方式"
        canonicalUrl="https://8090movie.com/about"
        jsonLd={aboutJsonLd}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "关于我们" },
            ]}
          />
        </div>

        {/* Page Header */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-primary-50 rounded-2xl">
              <i className="ri-film-line text-3xl text-primary-600"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于8090电影网
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              8090电影网怀旧影视专区，收录80后经典电影、90后电视剧、老电影在线观看，
              重温童年回忆剧，唤起青春记忆的专属片库。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full bg-white py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              品牌故事
            </h2>
            <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
              <p>
                8090电影网诞生于一群80后、90后影迷的共同心愿——在这个信息爆炸的时代，
                为那些曾经陪伴我们成长的经典影视作品留下一个温暖的家。
              </p>
              <p>
                还记得小时候搬着小板凳守在电视机前等《西游记》开播的日子吗？还记得和同学们
                一起讨论《还珠格格》剧情到深夜的时光吗？还记得第一次看《大话西游》时从笑到哭的感动吗？
              </p>
              <p>
                8090电影网怀旧影视专区，就是要把这些珍贵的记忆一一收藏。我们收录了
                <strong>80后经典电影</strong>、<strong>90后电视剧</strong>、
                童年动画、港片经典等各类怀旧影视内容。每一部作品都有详细的介绍、经典台词、
                幕后故事，让你重温那些曾经让你欢笑和流泪的瞬间。
              </p>
              <p>
                我们相信，好的影视作品不会因时间流逝而褪色。相反，经过岁月的沉淀，
                它们会像陈年老酒一样越发醇厚。8090电影网的使命，就是守护这份属于
                我们这代人的独特记忆，让<strong>童年回忆剧</strong>永远鲜活。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { num: "200+", label: "收录作品" },
                { num: "6", label: "影视分类" },
                { num: "10万+", label: "怀旧影迷" },
                { num: "1980", label: "最早收录年代" },
              ].map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 mb-2">
                    {stat.num}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-white py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              影迷口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 font-semibold text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.year}</div>
                    </div>
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">"{t.comment}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              有任何建议或想推荐经典影视作品？欢迎给我们留言
            </p>

            {formStatus === "success" ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">提交成功！</h3>
                <p className="text-green-700 text-sm">感谢你的留言，我们会尽快回复。</p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="mt-4 px-5 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-white rounded-xl border border-background-200 p-6 md:p-8"
              >
                {formStatus === "error" && (
                  <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {formError || "提交失败，请稍后重试"}
                  </div>
                )}

                <div className="mb-4">
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    你的名字
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入你的名字"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入你的留言或建议（最多500字）"
                  />
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-white font-medium text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}